import React, { useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { dateOnlyBodyTemplate } from '../../../../utils/dateUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import useTrainingInstructorsData from '../hooks/useTrainingInstructorsData';
import InstructorUserDropdown from '../../components/InstructorUserDropdown';

/**
 * Eğitim detayındaki "Eğitmenler" sekmesi. Bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 1.md §4.
 */
const InstructorsTab = ({ trainingId, readOnly = false }) => {
    const { th } = useLanguage();
    const { instructorsApi, isLoading, isAdding, isDeleting, addInstructor, removeInstructor } = useTrainingInstructorsData(trainingId);
    const [selectedUserId, setSelectedUserId] = useState(null);

    const instructors = useMemo(() => instructorsApi?.items || [], [instructorsApi]);
    const assignedUserIds = useMemo(() => instructors.map((row) => row.userId), [instructors]);

    const handleAdd = async () => {
        if (!selectedUserId) return;
        const success = await addInstructor(selectedUserId);
        if (success) setSelectedUserId(null);
    };

    const handleRemove = (row) => {
        confirmDialog({
            message: th('trainingCatalog.instructors.confirmDeleteMessage'),
            header: th('common.delete'),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => removeInstructor(row.id)
        });
    };

    const actionBodyTemplate = (row) => <i className="pi pi-trash cursor-pointer text-red-500 hover:text-red-700" style={{ fontSize: '1.1rem' }} title={th('common.delete')} onClick={() => handleRemove(row)} />;

    return (
        <div>
            <ConfirmDialog />
            {!readOnly && (
                <div className="flex align-items-end gap-2 mb-3">
                    <div style={{ minWidth: '20rem' }}>
                        <label className="block mb-1">{th('trainingCatalog.instructors.selectUser')}</label>
                        <InstructorUserDropdown value={selectedUserId} onChange={setSelectedUserId} excludeUserIds={assignedUserIds} placeholder={th('trainingCatalog.instructors.selectUserPlaceholder')} className="w-full" />
                    </div>
                    <Button label={th('trainingCatalog.instructors.addButton')} icon="pi pi-plus" onClick={handleAdd} disabled={!selectedUserId} loading={isAdding} />
                </div>
            )}

            <DataTable value={instructors} loading={isLoading || isDeleting} dataKey="id" emptyMessage={th('trainingCatalog.instructors.emptyMessage')} className="p-datatable-gridlines" showGridlines>
                {!readOnly && <Column body={actionBodyTemplate} style={{ width: '4rem' }} align="center" />}
                <Column field="userId" header={th('trainingCatalog.instructors.columnUserId')} style={{ width: '8rem' }} />
                <Column field="userFullName" header={th('trainingCatalog.instructors.columnUserFullName')} />
                <Column field="userEmail" header={th('trainingCatalog.instructors.columnUserEmail')} />
                <Column field="createdDate" header={th('trainingCatalog.instructors.columnAssignedDate')} style={{ width: '10rem' }} body={(row) => dateOnlyBodyTemplate(row, 'createdDate')} />
            </DataTable>
        </div>
    );
};

export default InstructorsTab;
